import { useEffect, useMemo, useState } from "react";

import { ContentCopy } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  getItemTypeOption,
  postItemTypeOption,
} from "@rubi/api-generated/clientRs/sdk.gen";
import { toast } from "@rubi/ui";

import { devEnvironment } from "@/common/featureFlags";
import { useServiceConfigurations } from "@/hooks/useCommunities";

interface CopySource {
  id: string;
  name: string;
}

interface CopyDropdownToOrgModalProps {
  open: boolean;
  onClose: () => void;
  source: CopySource | null;
  currentOrgId: string;
  onSuccess?: () => void;
}

const CopyDropdownToOrgModal = ({
  open,
  onClose,
  source,
  currentOrgId,
  onSuccess,
}: CopyDropdownToOrgModalProps) => {
  const [targetOrgId, setTargetOrgId] = useState("");
  const [name, setName] = useState("");
  const [copying, setCopying] = useState(false);

  // Destination orgs come from the community directory (same source as the "Viewing as:" switcher),
  // so the picker never needs a GET /org call. The current org is filtered out — same-org copies use
  // Duplicate.
  const serviceConfigurations = useServiceConfigurations();
  const targetOrgs = useMemo(() => {
    const isDev = devEnvironment();
    return Object.values(serviceConfigurations)
      .filter((cfg) => cfg.id)
      .map((cfg) => ({
        id: isDev ? cfg.testID : cfg.id,
        name: cfg.fullName ?? cfg.shortName ?? "Unknown",
      }))
      .filter((org) => org.id !== currentOrgId)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [currentOrgId, serviceConfigurations]);

  // Reset the form each time the modal opens for a new source list.
  useEffect(() => {
    if (open) {
      setTargetOrgId("");
      setName("");
    }
  }, [open, source?.id]);

  const handleClose = () => {
    if (copying) return;
    onClose();
  };

  const handleCopy = async () => {
    if (!source || !targetOrgId) return;
    setCopying(true);
    try {
      // Read the source list (returned in v2 shape, legacy rows converted on the fly),
      // then create it under the target org. Blank name keeps the original.
      const sourceList = await getItemTypeOption({
        path: { item_type_option_id: source.id },
      });
      const items = sourceList.data?.items;
      if (!items) throw new Error("Could not load the source list");

      const response = await postItemTypeOption({
        body: {
          rubi_org_id: targetOrgId,
          name: name.trim() || source.name || undefined,
          items,
        },
      });

      const targetOrgName =
        targetOrgs.find((org) => org.id === targetOrgId)?.name ??
        "the selected organization";
      const copiedName =
        response.data?.item_type_option?.name || source.name || "list";
      toast.success(`Copied "${copiedName}" to ${targetOrgName}`);
      onSuccess?.();
      onClose();
    } catch (err: any) {
      console.error("Failed to copy dropdown list to org:", err);
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to copy list";
      toast.error(message);
    } finally {
      setCopying(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Copy list to another organization</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Create a copy of <strong>{source?.name || "this list"}</strong> —
          including its items and translations — in another organization. The
          original is left untouched.
        </Typography>

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel id="copy-target-org-label">
            Destination organization
          </InputLabel>
          <Select
            labelId="copy-target-org-label"
            label="Destination organization"
            value={targetOrgId}
            onChange={(e) => setTargetOrgId(e.target.value)}
          >
            {targetOrgs.map((org) => (
              <MenuItem key={org.id} value={org.id}>
                {org.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          size="small"
          label="Name for the copy (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={source?.name || ""}
          helperText="Leave blank to keep the original name."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={copying}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCopy}
          disabled={copying || !targetOrgId}
          startIcon={
            copying ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <ContentCopy />
            )
          }
        >
          {copying ? "Copying..." : "Copy"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CopyDropdownToOrgModal;
